<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="loading" />

  <div v-if="!loading" class="container-fluid my-3">
    <!-- Actions bar (hidden on print) -->
    <div class="d-flex align-items-center justify-content-between mb-3 no-print flex-wrap gap-2">
      <h4 class="mb-0">طباعة الباركود — {{ gradeName }} ({{ students.length }} طالب)</h4>
      <div class="d-flex gap-2">
        <button class="btn btn-danger" v-print="'#barcodePrintArea'">
          <i class="pi pi-file-pdf me-1"></i> تصدير PDF / طباعة
        </button>
        <router-link :to="`/grades/${gradeSlug}/students/barcodes`" class="btn btn-secondary">
          <i class="pi pi-arrow-right me-1"></i> رجوع
        </router-link>
      </div>
    </div>

    <p class="no-print text-muted small" v-if="activeFiltersLabel">
      الفلاتر المطبّقة: {{ activeFiltersLabel }}
    </p>

    <!-- Printable area -->
    <div id="barcodePrintArea">
      <div class="print-title">
        <h2>طلاب الصف {{ gradeName }}</h2>
      </div>

      <div class="barcode-grid">
        <div
          v-for="student in students"
          :key="student.user_id"
          class="barcode-card"
        >
          <QrCodeLabel :value="student.qr_url" :size="170" />
          <div class="student-name">{{ student.name }}</div>
          <div class="student-reservation">رقم الحجز: {{ student.reservation_number }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import QrCodeLabel from '@/components/global/QrCodeLabel.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: 'StudentsBarcodesPrint',
  components: { Breadcrumb, Loading, QrCodeLabel },

  data() {
    return {
      students: [],
      loading: false,
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },

    gradeSlug() { return this.$route.params.gradeSlug; },

    grade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          ?? this.gradeStore.allGrades.find(g => g.slug === slug)
          ?? null;
    },

    gradeName() { return this.grade?.name ?? this.gradeSlug ?? ''; },

    // Filters carried over from the list page's "طباعة الكل" link (?word=&status=&study_mode=)
    // so the printed population always matches what the admin had filtered on-screen.
    word() { return this.$route.query.word || ''; },
    statusFilter() { return this.$route.query.status || ''; },
    studyModeFilter() { return this.$route.query.study_mode || ''; },

    activeFiltersLabel() {
      const parts = [];
      if (this.word) parts.push(`بحث: "${this.word}"`);
      if (this.statusFilter === '1') parts.push('الحالة: المقبولين');
      if (this.studyModeFilter === 'online') parts.push('النوع: أونلاين');
      if (this.studyModeFilter === 'offline') parts.push('النوع: أوفلاين');
      if (this.studyModeFilter === 'hybrid') parts.push('النوع: اونلاين +اوفلاين');
      return parts.join(' — ');
    },

    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: this.gradeName, path: `/grades/${this.gradeSlug}/students` },
        { name: 'باركود الطلاب', path: `/grades/${this.gradeSlug}/students/barcodes` },
        { name: 'طباعة الكل', path: this.$route.fullPath },
      ];
    },
  },

  methods: {
    async fetchStudents() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append('order_by_name', 1);
        fd.append('show_all', 1);
        if (this.grade?.id) fd.append('academic_year_id', this.grade.id);
        if (this.word) fd.append('word', this.word);
        if (this.statusFilter) fd.append('status', this.statusFilter);
        if (this.studyModeFilter) fd.append('study_mode', this.studyModeFilter);

        const { data } = await axios.post('/all_reservations?page=1', fd);
        const raw = data?.data?.data ?? [];
        this.students = raw.map(s => ({
          user_id: s.user_id ?? s.id,
          name: s.name,
          reservation_number: s.reservation_number,
          qr_url: s.qr_url,
        })).filter(s => s.qr_url);
      } finally {
        this.loading = false;
      }
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    if (!this.gradeStore.allGrades.length)    await this.gradeStore.fetchAllGrades();
    await this.fetchStudents();
  },
};
</script>

<style scoped>
/* ── Screen layout ── */
.barcode-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  padding: 8px;
}

.barcode-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 8px 6px;
  background: #fff;
  overflow: hidden;
  text-align: center;
}

.student-name {
  font-weight: 700;
  margin-top: 6px;
}

.student-reservation {
  font-size: 0.85rem;
  color: #555;
}

.print-title {
  text-align: center;
  margin-bottom: 20px;
  font-weight: 700;
}

/* ── Print styles ── */
@media print {
  .no-print {
    display: none !important;
  }

  #barcodePrintArea {
    padding: 0;
  }

  .print-title h2 {
    font-size: 16pt;
    margin-bottom: 10pt;
  }

  /* QR needs to stay large enough to scan reliably after printing —
     3 per row (not 4, like the old smaller barcode grid) gives each
     code more physical size on the page. */
  .barcode-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8pt;
    padding: 0;
  }

  .barcode-card {
    border: 1pt solid #000;
    border-radius: 3pt;
    padding: 8pt 6pt 6pt;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: hidden;
  }

  .student-name {
    font-size: 11pt;
  }

  .student-reservation {
    font-size: 9pt;
  }

  canvas {
    max-width: 100% !important;
    height: auto !important;
  }
}
</style>
